import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckCircle, XCircle, Clock, Users, Trash2, Filter } from 'lucide-react';

interface UserProfile {
  id: string;
  email: string;
  approval_status: string;
  created_at: string;
  phone_number: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  rejection_reason: string | null;
}

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

export const UserManagement = () => {
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  
  // Initialize filter from URL param or default to 'pending'
  const initialFilter = (searchParams.get('filter') as StatusFilter) || 'pending';
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    ['all', 'pending', 'approved', 'rejected'].includes(initialFilter) ? initialFilter : 'pending'
  );
  
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, approval_status, created_at, phone_number, street, city, state, zip, rejection_reason')
      .order('created_at', { ascending: false });

    if (error) {
      // Log only in development to prevent info leakage
      if (import.meta.env.DEV) {
        console.error('Error fetching users:', error);
      }
      toast.error('Failed to load users');
      return;
    }

    setUsers((data || []) as UserProfile[]);
    setLoading(false);
  };

  const updateUserStatus = async (userId: string, newStatus: string) => {
    const { error } = await supabase
      .from('profiles')
      .update({ approval_status: newStatus })
      .eq('id', userId);

    if (error) {
      // Log only in development to prevent info leakage
      if (import.meta.env.DEV) {
        console.error('Error updating user status:', error);
      }
      toast.error('Failed to update user status');
      return;
    }

    toast.success(`User ${newStatus === 'approved' ? 'approved' : 'rejected'} successfully`);
    fetchUsers();
  };

  const handleRejectClick = (user: UserProfile) => {
    setSelectedUser(user);
    setRejectionReason(user.rejection_reason || '');
    setRejectDialogOpen(true);
  };

  const handleRejectWithReason = async () => {
    if (!selectedUser) return;
    
    setIsRejecting(true);
    
    const { error } = await supabase
      .from('profiles')
      .update({ 
        approval_status: 'rejected',
        rejection_reason: rejectionReason.trim() || null
      })
      .eq('id', selectedUser.id);

    if (error) {
      // Log only in development to prevent info leakage
      if (import.meta.env.DEV) {
        console.error('Error rejecting user:', error);
      }
      toast.error('Failed to reject user');
      setIsRejecting(false);
      return;
    }

    toast.success('User rejected successfully');
    setRejectDialogOpen(false);
    setSelectedUser(null);
    setRejectionReason('');
    setIsRejecting(false);
    fetchUsers();
  };

  const deleteUser = async (userId: string) => {
    setDeletingUserId(userId);
    
    try {
      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: { userId }
      });

      if (error) {
        // Log only in development to prevent info leakage
        if (import.meta.env.DEV) {
          console.error('Error deleting user:', error);
        }
        toast.error('Failed to delete user');
        return;
      }

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      // Log only in development to prevent info leakage
      if (import.meta.env.DEV) {
        console.error('Error deleting user:', error);
      }
      toast.error('Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case 'approved':
        return (
          <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredUsers = users.filter(user => {
    if (statusFilter === 'all') return true;
    return user.approval_status === statusFilter;
  });

  const pendingCount = users.filter(u => u.approval_status === 'pending').length;
  const approvedCount = users.filter(u => u.approval_status === 'approved').length;
  const rejectedCount = users.filter(u => u.approval_status === 'rejected').length;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">Loading users...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              User Management
              {pendingCount > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {pendingCount} pending
                </Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={(value: StatusFilter) => setStatusFilter(value)}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users ({users.length})</SelectItem>
                  <SelectItem value="pending">Pending ({pendingCount})</SelectItem>
                  <SelectItem value="approved">Approved ({approvedCount})</SelectItem>
                  <SelectItem value="rejected">Rejected ({rejectedCount})</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredUsers.length === 0 ? (
            <p className="text-muted-foreground">
              {statusFilter === 'all' ? 'No users registered yet.' : `No ${statusFilter} users found.`}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden md:table-cell">Phone</TableHead>
                    <TableHead className="hidden lg:table-cell">Address</TableHead>
                    <TableHead className="hidden sm:table-cell">Registered</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium text-xs sm:text-sm max-w-[120px] sm:max-w-none truncate">
                        {user.email}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {getStatusBadge(user.approval_status)}
                          {user.approval_status === 'rejected' && user.rejection_reason && (
                            <span className="text-xs text-muted-foreground max-w-[150px] truncate" title={user.rejection_reason}>
                              Reason: {user.rejection_reason}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {user.phone_number || 'Not provided'}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {user.street || user.city || user.state || user.zip ? (
                          <div className="text-sm">
                            {user.street && <div>{user.street}</div>}
                            <div>
                              {[user.city, user.state, user.zip].filter(Boolean).join(', ') || 'Incomplete'}
                            </div>
                          </div>
                        ) : (
                          'Not provided'
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 sm:gap-2 flex-wrap">
                          {user.approval_status !== 'approved' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 hover:bg-green-50 h-8 px-2 sm:px-3"
                              onClick={() => updateUserStatus(user.id, 'approved')}
                            >
                              <CheckCircle className="w-4 h-4 sm:mr-1" />
                              <span className="hidden sm:inline">Approve</span>
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50 h-8 px-2 sm:px-3"
                            onClick={() => handleRejectClick(user)}
                          >
                            <XCircle className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">
                              {user.approval_status === 'rejected' ? 'Edit Reason' : 'Reject'}
                            </span>
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-destructive hover:bg-destructive/10 h-8 px-2 sm:px-3"
                                disabled={deletingUserId === user.id}
                              >
                                <Trash2 className="w-4 h-4 sm:mr-1" />
                                <span className="hidden sm:inline">Delete</span>
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete User</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to permanently delete <strong>{user.email}</strong>? 
                                  This action cannot be undone and will remove all their data including packages and schedules.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteUser(user.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete User
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject User</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting <strong>{selectedUser?.email}</strong>. 
              This message will be visible to the user.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">Rejection Reason</Label>
              <Textarea
                id="rejection-reason"
                placeholder="e.g., Incomplete information, service area not covered, etc."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRejectWithReason}
              disabled={isRejecting}
            >
              {isRejecting ? 'Rejecting...' : 'Reject User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};